import { memo, useCallback } from "react";
import type { Store } from "../../shared/types";
import { AREA_META } from "../../shared/types";
import { cn } from "../lib/classNames";
import { getTier, TIER_LIST_BADGE_CLASS } from "../lib/tier";
import { SelectionBadges } from "./SelectionBadges";

interface Props {
  stores: Store[];
  isLoading: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export function StoreList({ stores, isLoading, selectedId, onSelect }: Props) {
  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-6 text-sm text-slate-400">
        読み込み中...
      </div>
    );
  }
  if (stores.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-slate-400">
        該当する店舗がありません
      </div>
    );
  }

  // 各行の再レンダリング条件は `store` / `active` / `onSelect` のみ。
  // progressive resolve の batch tick で resolved が増えても stores 配列は変わらない
  // ため、親の再レンダリングが発生しても memo された行は shallow 比較で skip される。
  return (
    <ul className="thin-scroll flex-1 overflow-y-auto">
      {stores.map((store) => (
        <StoreListRow
          key={store.id}
          store={store}
          active={selectedId === store.id}
          onSelect={onSelect}
        />
      ))}
    </ul>
  );
}

interface RowProps {
  store: Store;
  active: boolean;
  onSelect: (id: string) => void;
}

const StoreListRow = memo(function StoreListRow({
  store,
  active,
  onSelect,
}: RowProps) {
  const handleClick = useCallback(() => onSelect(store.id), [onSelect, store.id]);

  // 移転先が閉店している場合は閉店扱いに倒す (StoreMarker / StoreSheet と同条件)。
  // renewed も見た目は閉店扱い (line-through) だがバッジは「リニューアル」。
  const isClosed = store.status === "closed" || store.movedToClosed === true;
  const isRenewed = store.status === "renewed";
  const isInactive = isClosed || isRenewed;

  return (
    <li>
      <button
        type="button"
        onClick={handleClick}
        className={cn(
          "flex w-full flex-col gap-1.5 border-b border-white/5 px-4 py-3 text-left transition",
          active
            ? "bg-rose-500/10 hover:bg-rose-500/15"
            : "hover:bg-white/5",
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <span
            className={cn(
              "text-[13.5px] font-semibold leading-snug",
              isInactive
                ? "text-slate-500 line-through decoration-slate-600"
                : "text-slate-100",
            )}
          >
            {store.name}
          </span>
          <div className="flex shrink-0 items-center gap-1">
            {isClosed && (
              <span className="rounded-full border border-slate-600/60 bg-slate-800/80 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-slate-400">
                閉店
              </span>
            )}
            {isRenewed && (
              <span className="rounded-full border border-slate-600/60 bg-slate-800/80 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-slate-400">
                リニューアル
              </span>
            )}
            <TierBadge count={store.selectionCount} />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-400">
          <span>{AREA_META[store.area].label}</span>
          {store.neighborhood && (
            <>
              <span className="text-slate-600">·</span>
              <span className="truncate">{store.neighborhood}</span>
            </>
          )}
          <span className="text-slate-600">·</span>
          <span>
            {store.firstSelectedYear}
            {store.firstSelectedYear !== store.lastSelectedYear &&
              `-${store.lastSelectedYear}`}
          </span>
        </div>
        <SelectionBadges years={store.years} size="sm" />
      </button>
    </li>
  );
});

function TierBadge({ count }: { count: number }) {
  // 5 段階 tier (lib/tier.ts) を使って色を決める。マップピンと同じ tier 関数なので
  // バッジとピンの色が必ず一致する。
  const tier = getTier(count);
  return (
    <span
      className={cn(
        "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold",
        TIER_LIST_BADGE_CLASS[tier],
      )}
    >
      {count}回
    </span>
  );
}
